import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { object, string } from 'yup';

import { Sizes, Variants } from '@common/types';
import { Input } from '@base-app';
import { yupResolver } from '@hookform/resolvers/yup';
import { UserToUpdate } from '@modules/teams/types/UpdateTeamMember';
import { ButtonStyled, FormFieldRoleStyled, FormFieldStyled, FormStyled } from './UpdateTeamMemberForm.styles';
import { UpdateTeamMemberFormProps } from './UpdateTeamMemberForm.types';
import { useUserFieldsValidation } from '@modules/shared/hooks/useUserFieldsValidation';

const UpdateTeamMemberForm: FC<UpdateTeamMemberFormProps> = ({ onChange, onUpdateForm, user }) => {
  const { t } = useTranslation(['common', 'teams']);
  const { firstName, lastName, email } = useUserFieldsValidation();

  const schema = object({
    firstName,
    lastName,
    email,
    role: string(),
  });

  const defaultValues = {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    role: user.role,
  };

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    getValues,
  } = useForm<UserToUpdate>({
    resolver: yupResolver(schema),
    defaultValues,
    reValidateMode: 'onSubmit',
  });

  const handleFormSubmit: SubmitHandler<UserToUpdate> = (values) => {
    onUpdateForm({
      ...values,
    });
  };

  return (
    <FormStyled onChange={() => onChange?.(getValues())} onSubmit={handleSubmit(handleFormSubmit)}>
      <FormFieldStyled css={{ '@sm': { paddingRight: '$8' } }}>
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              testId="first-name-input"
              id={field.name}
              name={field.name}
              label={t('first_name', { ns: 'teams' })}
              fullWidth
              error={!(errors.firstName == null)}
              errorDescription={errors.firstName?.message as string}
              placeholder={t('first_name_example', { ns: 'teams' })}
            />
          )}
        />
      </FormFieldStyled>
      <FormFieldStyled>
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              testId="last-name-input"
              id={field.name}
              name={field.name}
              label={t('last_name', { ns: 'teams' })}
              fullWidth
              error={!(errors.lastName == null)}
              errorDescription={errors.lastName?.message as string}
              placeholder={t('last_name_example', { ns: 'teams' })}
            />
          )}
        />
      </FormFieldStyled>
      <FormFieldStyled css={{ '@sm': { paddingRight: '$8' } }}>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              testId="email-input"
              id={field.name}
              name={field.name}
              label={t('email', { ns: 'teams' })}
              type="email"
              fullWidth
              error={!(errors.email == null)}
              errorDescription={errors.email?.message as string}
              placeholder={t('email_example', { ns: 'teams' })}
            />
          )}
        />
      </FormFieldStyled>
      <FormFieldRoleStyled>
        <ButtonStyled type="submit" size={Sizes.SMALL} variant={Variants.SECONDARY} disabled={!isDirty}>
          {t('update')}
        </ButtonStyled>
      </FormFieldRoleStyled>
    </FormStyled>
  );
};

export default UpdateTeamMemberForm;
